/**
 * Página principal de Gestión de Recetas
 * Dashboard nutricional con calculadora y gestión de recetas complejas
 */
import React, { useState, useEffect } from "react";
import {
  Card,
  Typography,
  Table,
  Space,
  Button,
  message,
  Tag,
  Popconfirm,
  Alert,
} from "antd";
import {
  InfoCircleOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CalculatorOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { useBranch } from "../context/BranchContext";
import api from "../api/endpoints";
import RecipeForm from "../components/nutritional/RecipeForm";

const { Title } = Typography;

const Recipes = () => {
  const { activeBranch } = useBranch();
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedRecipe, setSelectedRecipe] = useState(null);

  const loadRecipes = async () => {
    setLoading(true);
    try {
      console.log("Cargando recetas con filtros:", {
        recipe_type: "NUTRITIONAL_RECIPE",
        branch_id: activeBranch,
      });
      const response = await api.recipes.list(1, {
        recipe_type: "NUTRITIONAL_RECIPE",
        branch_id: activeBranch,
      });
      console.log("Recetas recibidas:", response.results);
      console.log("Total de recetas:", response.count);
      setRecipes(response.results || []);
    } catch (error) {
      console.error("Error cargando recetas:", error);
      message.error("Error al cargar recetas");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRecipes();
  }, [activeBranch]);

  const handleCreate = () => {
    setSelectedRecipe(null);
    setModalVisible(true);
  };

  const handleEdit = (recipe) => {
    setSelectedRecipe(recipe);
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      await api.recipes.destroy(id);
      message.success("Receta eliminada exitosamente");
      loadRecipes();
    } catch (error) {
      message.error("Error al eliminar receta");
    }
  };

  const handleCalculateNutrition = async (id) => {
    try {
      setLoading(true);
      await api.recipes.calculateNutrition(id);
      message.success("Nutrición calculada exitosamente");
      loadRecipes();
    } catch (error) {
      message.error("Error al calcular nutrición");
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: "Código",
      dataIndex: "code",
      key: "code",
      width: 150,
    },
    {
      title: "Nombre",
      dataIndex: "name",
      key: "name",
      ellipsis: true,
    },
    {
      title: "Rendimiento",
      key: "yield",
      width: 150,
      render: (_, record) => (
        <Tag color="cyan">
          {record.total_yield_grams ? `${record.total_yield_grams}g` : "-"}
        </Tag>
      ),
    },
    {
      title: "Estado Nutricional",
      key: "nutrition_status",
      width: 180,
      render: (_, record) => (
        <Tag color={record.has_nutritional_calculation ? "green" : "orange"}>
          {record.has_nutritional_calculation ? "✓ Calculada" : "⚠ Sin calcular"}
        </Tag>
      ),
    },
    {
      title: "Acciones",
      key: "actions",
      width: 250,
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Editar
          </Button>
          {!record.has_nutritional_calculation && (
            <Button
              type="link"
              icon={<CalculatorOutlined />}
              onClick={() => handleCalculateNutrition(record.id)}
            >
              Calcular
            </Button>
          )}
          <Popconfirm
            title="¿Eliminar esta receta?"
            onConfirm={() => handleDelete(record.id)}
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              Eliminar
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card>
        <Title level={2} style={{ marginBottom: 24 }}>
          Mis Recetas de Helado
        </Title>

        <Alert
          message="¿Cómo funciona?"
          description="Aquí creas tus recetas de helado. Solo necesitas decir qué ingredientes usas y cuánto de cada uno. El sistema calculará automáticamente cuántas calorías, proteínas y grasas tiene tu helado, y podrás generar la etiqueta nutricional que va en tus productos."
          type="info"
          icon={<InfoCircleOutlined />}
          style={{ marginBottom: 24 }}
          showIcon
        />

        <Space wrap style={{ marginBottom: 16, width: "100%" }}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleCreate}
            className="mobile-full-width"
          >
            Crear Nueva Receta
          </Button>
          <Button 
            icon={<ReloadOutlined />} 
            onClick={loadRecipes}
            className="mobile-full-width"
          >
            Actualizar
          </Button>
        </Space>

        <Table
          columns={columns}
          dataSource={recipes}
          loading={loading}
          rowKey="id"
          pagination={{ 
            pageSize: 10,
            responsive: true,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50"],
          }}
          scroll={{ x: 800 }}
          responsive
        />
      </Card>

      <RecipeForm
        visible={modalVisible}
        recipe={selectedRecipe}
        onCancel={() => {
          setModalVisible(false);
          setSelectedRecipe(null);
        }}
        onSuccess={() => {
          setModalVisible(false);
          setSelectedRecipe(null);
          // Pequeño delay para asegurar que localStorage se haya actualizado
          setTimeout(() => {
            loadRecipes();
          }, 100);
        }}
      />
    </div>
  );
};

export default Recipes;

